import { ListDropdown } from "./list-dropdown";

export type SuggestionSource = (term: string) => Promise<string[]>;

export class SuggestionsDropdown
{
    private static readonly delay = 250;

    private readonly input: HTMLInputElement;
    private readonly dropdown: ListDropdown;
    private readonly source: SuggestionSource;

    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private requestToken = 0;

    constructor(input: HTMLInputElement, host: HTMLElement, source: SuggestionSource)
    {
        this.input = input;
        this.source = source;

        // the input should not get the browser's native autocomplete on top of ours
        this.input.autocomplete = "off";

        this.dropdown = new ListDropdown(
            input,
            host,
            (value) =>
            {
                this.input.value = value;
                this.input.dispatchEvent(new Event("change"));
            },
            () => this.scheduleSearch()
        );

        // cancel a pending search when leaving the field, so a debounced search
        // can't pop the menu open again after it has been closed on blur
        this.input.addEventListener("blur", () => this.cancelPendingSearch());
    }

    private scheduleSearch()
    {
        this.cancelPendingSearch();
        this.debounceTimer = setTimeout(() => this.search(), SuggestionsDropdown.delay);
    }

    private cancelPendingSearch()
    {
        if (this.debounceTimer !== null)
        {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    private async search()
    {
        const token = ++this.requestToken;
        let results: string[];
        try
        {
            results = await this.source(this.input.value);
        }
        catch
        {
            results = [];
        }

        // ignore responses that have been superseded by a newer request
        if (token !== this.requestToken)
        {
            return;
        }

        const items = results ?? [];
        this.dropdown.setItems(items);
        // only open the menu if the field is still focused — the request may resolve
        // after the user has tabbed/clicked away (and the menu was hidden on blur).
        // setItems hides the menu when empty, so only the open case is left here
        if (items.length > 0 && document.activeElement === this.input)
        {
            this.dropdown.showMenu();
        }
    }
}
